import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Container, Grid, Typography, Box } from "@mui/material";
import { useSnackbar } from "notistack";
import dayjs from "dayjs";
import { STATUS } from "../../../../../redux/constants";
import { resetAddCombustionStatus } from "../../../../../redux/actions";
import useStyles from "./styles";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import CeroItemPair from "../../../../../components/CeroItemPair";

const DownstreamTAndD = (props) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const { emissionData, onCancel } = props;

  const deleteEmissionData = useSelector(
    (state) => state.emission.deleteEmissions
  );

  useEffect(() => {
    if (deleteEmissionData.status === STATUS.SUCCESS) {
      enqueueSnackbar("Downstream T and D record deleted successfully", {
        variant: "success",
      });
      dispatch(resetAddCombustionStatus());
      onCancel();
    } else if (deleteEmissionData.status === STATUS.ERROR) {
      enqueueSnackbar(
        deleteEmissionData.message.message ||
          "We couldn't process your request. Please try again later.",
        { variant: "error" }
      );
      dispatch(resetAddCombustionStatus());
    }
  }, [deleteEmissionData, enqueueSnackbar, onCancel, dispatch]);

  const monthDict = {
    1: "January",
    2: "February",
    3: "March",
    4: "April",
    5: "May",
    6: "June",
    7: "July",
    8: "August",
    9: "September",
    10: "October",
    11: "November",
    12: "December",
  };
  return (
    <>
      <div className={classes.headerContainer}>
        <div
          className={classes.backContainer}
          onClick={() => {
            navigate(-1);
          }}
        >
          <ArrowBackIosIcon />
          Back
        </div>
        <Box className={classes.alignButton}></Box>
      </div>
      <Container className={classes.container}>
        <Box className={classes.innerContainer}>
          <Box className={classes.header}>
            <Typography variant="h6" component="div">
              Downstream T&D
            </Typography>
          </Box>
          <Box className={classes.topContainer}>
            <Grid
              container
              direction={"row"}
              wrap="nowrap"
              justifyContent={"space-between"}
              display={"contents"}
              spacing={12}
            >
              <Grid
                item
                container
                direction={"column"}
                display={"contents"}
                xs={10}
              >
                <CeroItemPair
                  title="Facility:"
                  value={
                    emissionData.facility_name
                      ? emissionData.facility_name
                      : "NA"
                  }
                  classes={{
                    container: classes.infoContainer,
                    title: classes.diversityTitle,
                    value: classes.value,
                  }}
                />

                <CeroItemPair
                  title="Month and Year:"
                  value={
                    emissionData.year && emissionData.month
                      ? monthDict[emissionData.month] + " " + emissionData.year
                      : "NA"
                  }
                  classes={{
                    container: classes.infoContainer,
                    title: classes.diversityTitle,
                    value: classes.value,
                  }}
                />
                <CeroItemPair
                  title="Downstream Type:"
                  value={
                    emissionData.calculation_method_name &&
                    [
                      "distance-based",
                      "fuel-based",
                      "refrigerant leakage",
                      "spend-based",
                    ].includes(
                      emissionData.calculation_method_name.toLowerCase()
                    )
                      ? "Transportation"
                      : "Distribution"
                  }
                  classes={{
                    container: classes.infoContainer,
                    title: classes.diversityTitle,
                    value: classes.value,
                  }}
                />
                <CeroItemPair
                  title="Calculation Method:"
                  value={
                    emissionData.calculation_method_name
                      ? emissionData.calculation_method_name
                      : "NA"
                  }
                  classes={{
                    container: classes.infoContainer,
                    title: classes.diversityTitle,
                    value: classes.value,
                  }}
                />
                {emissionData?.calculation_method_name?.toLowerCase() ===
                "fuel-based" ? (
                  <>
                    <CeroItemPair
                      title="Fuel Source:"
                      value={
                        emissionData.fuel_source_name
                          ? emissionData.fuel_source_name
                          : "NA"
                      }
                      classes={{
                        container: classes.infoContainer,
                        title: classes.diversityTitle,
                        value: classes.value,
                      }}
                    />
                    <CeroItemPair
                      title="Amount of Fuel(unit):"
                      value={
                        emissionData.amount_fuel
                          ? `${emissionData.amount_fuel} ${emissionData.fuel_unit}`
                          : "NA"
                      }
                      classes={{
                        container: classes.infoContainer,
                        title: classes.diversityTitle,
                        value: classes.value,
                      }}
                    />
                  </>
                ) : emissionData?.calculation_method_name?.toLowerCase() ===
                  "distance-based" ? (
                  <>
                    <CeroItemPair
                      title="Mode of transport:"
                      value={
                        emissionData.mode_of_transport_name
                          ? emissionData.mode_of_transport_name
                          : "NA"
                      }
                      classes={{
                        container: classes.infoContainer,
                        title: classes.diversityTitle,
                        value: classes.value,
                      }}
                    />
                    <CeroItemPair
                      title="Vehicle Type:"
                      value={
                        emissionData.vehicle_type_name
                          ? emissionData.vehicle_type_name
                          : "NA"
                      }
                      classes={{
                        container: classes.infoContainer,
                        title: classes.diversityTitle,
                        value: classes.value,
                      }}
                    />
                    <CeroItemPair
                      title="Emission Factor Dataset:"
                      value={
                        emissionData.ef_dataset_name
                          ? emissionData.ef_dataset_name
                          : "NA"
                      }
                      classes={{
                        container: classes.infoContainer,
                        title: classes.diversityTitle,
                        value: classes.value,
                      }}
                    />
                    <CeroItemPair
                      title="Distance Travelled:"
                      value={
                        emissionData.distance_travelled
                          ? `${emissionData.distance_travelled} ${emissionData.distance_unit}`
                          : "NA"
                      }
                      classes={{
                        container: classes.infoContainer,
                        title: classes.diversityTitle,
                        value: classes.value,
                      }}
                    />
                    <CeroItemPair
                      title="Mass of Purchased Goods:"
                      value={
                        emissionData.mass_purchased_goods
                          ? `${emissionData.mass_purchased_goods} ${emissionData.mass_unit}`
                          : "NA"
                      }
                      classes={{
                        container: classes.infoContainer,
                        title: classes.diversityTitle,
                        value: classes.value,
                      }}
                    />
                  </>
                ) : emissionData?.calculation_method_name?.toLowerCase() ===
                  "spend-based" ? (
                  <>
                    <CeroItemPair
                      title="Mode of transport:"
                      value={
                        emissionData.mode_of_transport_name
                          ? emissionData.mode_of_transport_name
                          : "NA"
                      }
                      classes={{
                        container: classes.infoContainer,
                        title: classes.diversityTitle,
                        value: classes.value,
                      }}
                    />

                    <CeroItemPair
                      title="Total Spend:"
                      value={
                        emissionData.total_spent
                          ? `${emissionData.total_spent} ${emissionData.currency}`
                          : "NA"
                      }
                      classes={{
                        container: classes.infoContainer,
                        title: classes.diversityTitle,
                        value: classes.value,
                      }}
                    />
                  </>
                ) : emissionData?.calculation_method_name?.toLowerCase() ===
                  "refrigerant leakage" ? (
                  <>
                    <CeroItemPair
                      title="Gas Type:"
                      value={
                        emissionData.gas_type_name
                          ? emissionData.gas_type_name
                          : "NA"
                      }
                      classes={{
                        container: classes.infoContainer,
                        title: classes.diversityTitle,
                        value: classes.value,
                      }}
                    />
                    <CeroItemPair
                      title="Refrigerant:"
                      value={
                        emissionData.refrigerant_name
                          ? emissionData.refrigerant_name
                          : "NA"
                      }
                      classes={{
                        container: classes.infoContainer,
                        title: classes.diversityTitle,
                        value: classes.value,
                      }}
                    />

                    <CeroItemPair
                      title="Quantity of refrigerant leakage(Unit):"
                      value={
                        emissionData.amount_refrigerants
                          ? `${emissionData.amount_refrigerants} ${emissionData.refrigerant_unit}`
                          : "NA"
                      }
                      classes={{
                        container: classes.infoContainer,
                        title: classes.diversityTitle,
                        value: classes.value,
                      }}
                    />
                  </>
                ) : emissionData?.calculation_method_name?.toLowerCase() ===
                  "site-specific method" ? (
                  <>
                    <CeroItemPair
                      title="Fuel Source:"
                      value={
                        emissionData.fuel_source_name
                          ? emissionData.fuel_source_name
                          : "NA"
                      }
                      classes={{
                        container: classes.infoContainer,
                        title: classes.diversityTitle,
                        value: classes.value,
                      }}
                    />
                    <CeroItemPair
                      title="Amount of fuel(unit):"
                      value={
                        emissionData.amount_fuel
                          ? `${emissionData.amount_fuel} ${emissionData.fuel_unit}`
                          : "NA"
                      }
                      classes={{
                        container: classes.infoContainer,
                        title: classes.diversityTitle,
                        value: classes.value,
                      }}
                    />

                    <CeroItemPair
                      title="Amount of electricity consumed(Unit):"
                      value={
                        emissionData.amount_electricity
                          ? `${emissionData.amount_electricity} ${emissionData.electricity_unit}`
                          : "NA"
                      }
                      classes={{
                        container: classes.infoContainer,
                        title: classes.diversityTitle,
                        value: classes.value,
                      }}
                    />
                    <CeroItemPair
                      title="Gas Type:"
                      value={
                        emissionData.gas_type_name
                          ? emissionData.gas_type_name
                          : "NA"
                      }
                      classes={{
                        container: classes.infoContainer,
                        title: classes.diversityTitle,
                        value: classes.value,
                      }}
                    />
                    <CeroItemPair
                      title="Refrigerant:"
                      value={
                        emissionData.refrigerant_name
                          ? emissionData.refrigerant_name
                          : "NA"
                      }
                      classes={{
                        container: classes.infoContainer,
                        title: classes.diversityTitle,
                        value: classes.value,
                      }}
                    />

                    <CeroItemPair
                      title="Quantity of refrigerant leakage(Unit):"
                      value={
                        emissionData.amount_refrigerants
                          ? `${emissionData.amount_refrigerants} ${emissionData.refrigerant_unit}`
                          : "NA"
                      }
                      classes={{
                        container: classes.infoContainer,
                        title: classes.diversityTitle,
                        value: classes.value,
                      }}
                    />
                  </>
                ) : emissionData?.calculation_method_name?.toLowerCase() ===
                  "average-data method" ? (
                  <>
                    <CeroItemPair
                      title="Volume of stored goods:"
                      value={
                        emissionData.volume_stored
                          ? `${emissionData.volume_stored} ${emissionData.volume_unit}`
                          : "NA"
                      }
                      classes={{
                        container: classes.infoContainer,
                        title: classes.diversityTitle,
                        value: classes.value,
                      }}
                    />
                    <CeroItemPair
                      title="Emission factor:"
                      value={
                        emissionData.emission_factor
                          ? emissionData.emission_factor
                          : "NA"
                      }
                      classes={{
                        container: classes.infoContainer,
                        title: classes.diversityTitle,
                        value: classes.value,
                      }}
                    />

                    <CeroItemPair
                      title="Average number of goods stored:"
                      value={
                        emissionData.average_number_of_days
                          ? emissionData.average_number_of_days
                          : "NA"
                      }
                      classes={{
                        container: classes.infoContainer,
                        title: classes.diversityTitle,
                        value: classes.value,
                      }}
                    />
                  </>
                ) : (
                  <></>
                )}
                {emissionData?.audited_by && (
                  <>
                    <CeroItemPair
                      title="Audited by:"
                      value={
                        emissionData.audited_by ? emissionData.audited_by : "NA"
                      }
                      classes={{
                        container: classes.infoContainer,
                        title: classes.diversityTitle,
                        value: classes.value,
                      }}
                    />
                    <CeroItemPair
                      title="Audited on:"
                      value={
                        emissionData.audited_on
                          ? dayjs(emissionData.audited_on).format("DD/MM/YYYY")
                          : "NA"
                      }
                      classes={{
                        container: classes.infoContainer,
                        title: classes.diversityTitle,
                        value: classes.value,
                      }}
                    />
                  </>
                )}
              </Grid>
            </Grid>
          </Box>

          <Box className={classes.bottomContainer}>
            <Typography
              variant="h6"
              component="h6"
              className={classes.previewTitle}
            >
              Emission
            </Typography>

            <Grid container direction="row" spacing={4}>
              {/* LEFT COLUMN */}
              <Grid item container direction="column" xs={12} md={6}>
                {emissionData?.co2 != null && (
                  <Typography className={classes.previewItem}>
                    CO<sub>2</sub>: {emissionData.co2} tonnes
                  </Typography>
                )}
                {emissionData?.ch4 != null && (
                  <Typography className={classes.previewItem}>
                    CH<sub>4</sub>: {emissionData.ch4} tonnes
                  </Typography>
                )}
                {emissionData?.biofuel_co2 != null && (
                  <Typography className={classes.previewItem}>
                    BioFuel CO<sub>2</sub>: {emissionData.biofuel_co2} tonnes
                  </Typography>
                )}
                {emissionData?.co2e != null && (
                  <Typography className={classes.previewItem}>
                    CO<sub>2</sub>e: {emissionData.co2e} tonnes
                  </Typography>
                )}
              </Grid>

              {/* RIGHT COLUMN */}
              <Grid item container direction="column" xs={12} md={6}>
                {emissionData?.n2o != null && (
                  <Typography className={classes.previewItem}>
                    N<sub>2</sub>O: {emissionData.n2o} tonnes
                  </Typography>
                )}
                {emissionData?.ef != null && (
                  <Typography className={classes.previewItem}>
                    EF: {emissionData.ef} kgCO<sub>2</sub>e/unit
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Container>
    </>
  );
};

export default DownstreamTAndD;
